/**
 * `next-auth/react` shim.
 *
 * `TopBar`, `AdminScopeToggle`, the dashboard page and a good number of the 44
 * pages S2 still has to move all call `useSession()` / `signOut()`. Presenting
 * the FastAPI token session under next-auth's client API keeps those ports to
 * a one-line import change (FE-03) and means `next-auth` disappears from the
 * SPA's dependency graph without a rewrite (CUT-01).
 *
 *   -import { useSession, signOut } from "next-auth/react";
 *   +import { useSession, signOut } from "@/compat/next-auth-react";
 *
 * The `Session` shape mirrors `src/types/next-auth.d.ts` exactly: `user.id`,
 * `user.role`, `user.rentModuleAccess`, plus `name`/`email`.
 */
import { useMemo } from "react";
import { useAuth } from "@/lib/auth/AuthProvider";

export interface SessionUser {
  id: string;
  name: string | null;
  email: string | null;
  role: string;
  rentModuleAccess: boolean;
}

export interface Session {
  user: SessionUser;
}

/** next-auth's own three states, same strings. */
export type SessionStatus = "loading" | "authenticated" | "unauthenticated";

export interface UseSessionResult {
  data: Session | null;
  status: SessionStatus;
}

export function useSession(): UseSessionResult {
  const { user, status } = useAuth();
  return useMemo<UseSessionResult>(
    () => ({
      data: user
        ? {
            user: {
              id: user.id,
              name: user.name,
              email: user.email,
              role: user.role,
              rentModuleAccess: user.rentModuleAccess,
            },
          }
        : null,
      status,
    }),
    [user, status],
  );
}

/**
 * Imperative sign-out for call sites that are not inside a component (or that
 * were written as `onClick={() => signOut({ callbackUrl: "/login" })}`).
 *
 * The context cannot be read from a bare function, so the provider publishes
 * its `logout` here on mount. A `signOut()` that arrives before any provider
 * has mounted still lands the user on the login page — it just cannot revoke
 * a session it does not have a handle on.
 */
type LogoutFn = () => Promise<void>;
let registeredLogout: LogoutFn | null = null;

export function registerSignOut(fn: LogoutFn | null): void {
  registeredLogout = fn;
}

export interface SignOutOptions {
  callbackUrl?: string;
  redirect?: boolean;
}

export async function signOut(options: SignOutOptions = {}): Promise<void> {
  const { callbackUrl = "/login", redirect = true } = options;
  try {
    if (registeredLogout) await registeredLogout();
  } finally {
    if (redirect && typeof window !== "undefined") {
      window.location.assign(callbackUrl);
    }
  }
}

/**
 * `signIn` is intentionally NOT implemented.
 *
 * next-auth's `signIn("credentials", …)` is a redirect-driven flow bound to
 * `/api/auth/*`; the SPA signs in through `useAuth().login`, which is what
 * `LoginForm` now calls. Leaving a stub that silently did nothing would be a
 * worse failure than a compile error, so any component still reaching for it
 * has to be looked at during its S2 port.
 */
export function signIn(): never {
  throw new Error(
    "signIn() is not available in the SPA — use useAuth().login(email, password). " +
      "See frontend/web/src/lib/auth/AuthProvider.tsx.",
  );
}
